import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertOctagon,
  ArrowLeft,
  Ban,
  CheckCircle2,
  Pencil,
  Play,
  Plus,
  Users,
  X,
} from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../app/AuthContext";
import { useGoBack } from "../../shared/hooks/goBack";
import { api, ApiError } from "../../shared/api/client";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  ErrorState,
  InlineAlert,
  LoadingState,
  PageHeader,
  StatusBadge,
  cn,
} from "../../shared/ui";
import type { BurndownView, SprintDetail } from "../../shared/types/api";
import { BurndownChart } from "./BurndownChart";
import { CancelSprintDrawer } from "./CancelSprintDrawer";
import { CompleteSprintDrawer } from "./CompleteSprintDrawer";
import { AddToSprintDrawer } from "./AddToSprintDrawer";
import { EditSprintDrawer } from "./EditSprintDrawer";
import { SprintCarryoverCard } from "./SprintCarryoverCard";
import { SprintBoard } from "./SprintBoard";
import { usePublishCurrentProject } from "../../shared/hooks/currentProject";
import {
  SPRINT_STATUS_LABELS,
  SPRINT_STATUS_TONE,
  describeUtilisation,
  formatDaysRemaining,
  formatHours,
  formatPercent,
} from "./sprintVocabulary";

/**
 * One sprint (§49, §50, §101).
 *
 * <p>The page changes with the sprint's state because the questions do. Planning asks "does this
 * fit"; running asks "are we going to make it"; finished asks "what actually happened". Showing
 * all three at once would bury whichever one matters today.
 */
export function SprintDetailPage() {
  const { sprintId = "" } = useParams();
  const navigate = useNavigate();
  const { goBack, hasHistory } = useGoBack("/sprints");
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);

  const sprint = useQuery({
    queryKey: ["sprint", sprintId],
    queryFn: () => api.get<SprintDetail>(`/api/v1/sprints/${sprintId}`),
  });

  const burndown = useQuery({
    queryKey: ["burndown", sprintId],
    queryFn: () =>
      api.get<BurndownView>(`/api/v1/sprints/${sprintId}/burndown`),
    enabled: sprint.data?.sprint.status !== "PLANNED",
  });

  // A sprint carries its project's id but not its name; the drawer resolves the name itself.
  usePublishCurrentProject(sprint.data?.sprint.projectId);

  const reload = () => {
    void queryClient.invalidateQueries({ queryKey: ["sprint", sprintId] });
    void queryClient.invalidateQueries({ queryKey: ["burndown", sprintId] });
    void queryClient.invalidateQueries({ queryKey: ["sprints"] });
    void queryClient.invalidateQueries({ queryKey: ["velocity"] });
    void queryClient.invalidateQueries({ queryKey: ["backlog"] });
  };

  const act = async (action: "start") => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/sprints/${sprintId}/${action}`);
      reload();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : "Could not do that.",
      );
    } finally {
      setBusy(false);
    }
  };

  /**
   * Takes one item out of the sprint and back to the backlog.
   *
   * <p>Not a deletion, and worth being clear about: the item keeps its status, its estimate and
   * its history, and simply stops being promised for this sprint. If the sprint has already
   * started, what it committed to at the start is a snapshot and does not change — which is what
   * stops a sprint being made to look successful by quietly removing whatever it was going to
   * miss.
   */
  const removeFromSprint = async (workItemId: string) => {
    setRemoving(workItemId);
    setError(null);
    try {
      await api.post("/api/v1/backlog/move-to-sprint", {
        workItemIds: [workItemId],
        sprintId: null,
      });
      reload();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Could not take that out of the sprint.",
      );
    } finally {
      setRemoving(null);
    }
  };

  if (sprint.isPending) {
    return <LoadingState label="Loading sprint…" />;
  }
  if (sprint.isError) {
    return (
      <ErrorState error={sprint.error} onRetry={() => void sprint.refetch()} />
    );
  }

  const data = sprint.data;
  const status = data.sprint.status;
  const planning = status === "PLANNED";
  const running = status === "ACTIVE";
  const finished = status === "COMPLETED";

  const utilisation =
    data.capacity.availableHours > 0
      ? data.plannedHours / data.capacity.availableHours
      : 0;
  const fit = describeUtilisation(utilisation);

  return (
    <>
      {/* §50 puts the goal first, and the top bar is where it now goes, with the name. A sprint
          whose goal is "finish the tickets" has none — so say so where the goal would have been
          rather than leaving the bar to imply the sprint has one. */}
      <PageHeader
        title={data.sprint.name}
        subtitle={
          data.sprint.goal ?? (
            <span className="text-ink-400">
              No goal set — the review will have nothing to be held against.
            </span>
          )
        }
      />

      {/* Everything else about the sprint on one line, with its buttons at the end of it. */}
      <div className="mb-4 flex flex-wrap items-center gap-2 text-xs text-ink-500">
        <Button
          variant="ghost"
          size="sm"
          icon={<ArrowLeft className="h-4 w-4" />}
          className="-ml-2"
          onClick={goBack}
        >
          {hasHistory ? "Back" : "All sprints"}
        </Button>
        <StatusBadge tone={SPRINT_STATUS_TONE[status]}>
          {SPRINT_STATUS_LABELS[status]}
        </StatusBadge>
        {data.sprint.overrunning && (
          <StatusBadge tone="red">Past its end date</StatusBadge>
        )}
        <span>
          {data.sprint.startDate} → {data.sprint.endDate}
          {data.sprint.teamName && ` · ${data.sprint.teamName}`}
          {running &&
            ` · ${formatDaysRemaining(data.sprint.daysRemaining, status)}`}
        </span>

        <div className="ml-auto flex flex-wrap gap-2">
          {planning && can("SPRINT_START") && (
            <Button
              icon={<Play className="h-4 w-4" />}
              loading={busy}
              onClick={() => void act("start")}
            >
              Start sprint
            </Button>
          )}
          {running && can("SPRINT_COMPLETE") && (
            <Button
              icon={<CheckCircle2 className="h-4 w-4" />}
              onClick={() => setCompleting(true)}
            >
              Complete sprint
            </Button>
          )}
          {/*
           * Editable up to the start, and then only the goal. A sprint that has begun measured
           * its capacity and captured its commitment from these dates; moving them afterwards
           * would leave a record describing a sprint that never happened.
           */}
          {(planning || running) && can("SPRINT_CREATE") && (
            <Button
              variant="secondary"
              icon={<Pencil className="h-4 w-4" />}
              onClick={() => setEditing(true)}
            >
              {planning ? "Edit" : "Set goal"}
            </Button>
          )}
          {(planning || running) && can("SPRINT_COMPLETE") && (
            <Button
              variant="secondary"
              icon={<Ban className="h-4 w-4" />}
              onClick={() => setCancelling(true)}
            >
              Cancel
            </Button>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4">
          <InlineAlert tone="error">{error}</InlineAlert>
        </div>
      )}

      {status === "CANCELLED" && (
        <div className="mb-4">
          <InlineAlert tone="info">
            This sprint was cancelled and its work returned to the backlog. No
            metrics were recorded — a cancelled sprint measured nothing, and
            zeroes would drag the velocity trend down as though the team had
            committed and failed.
          </InlineAlert>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          {!planning && burndown.data && (
            <Card>
              <CardHeader
                title="Burndown"
                subtitle={
                  data.burndownBasis === "STORY_POINTS"
                    ? "In story points, as this project is configured."
                    : "In remaining hours, as this project is configured."
                }
              />
              <CardBody>
                <BurndownChart data={burndown.data} />
              </CardBody>
            </Card>
          )}

          <Card>
            <CardHeader
              title={planning ? "Selected work" : "Board"}
              subtitle={
                planning
                  ? "Pulled in from the backlog. Nothing is committed until the sprint starts."
                  : "Dragging a card changes its status, and every move is recorded."
              }
              action={
                (planning || running) &&
                can("BACKLOG_RANK") && (
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={<Plus className="h-4 w-4" />}
                    onClick={() => setAdding(true)}
                  >
                    Add work
                  </Button>
                )
              }
            />
            <CardBody>
              {data.items.length === 0 ? (
                <div className="py-6 text-center">
                  <p className="text-sm text-ink-500">
                    Nothing in this sprint yet.
                  </p>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="mt-3"
                    onClick={() => navigate("/backlog")}
                  >
                    Pull work from the backlog
                  </Button>
                </div>
              ) : planning ? (
                <ul className="divide-y divide-ink-100">
                  {data.items.map((item) => (
                    <li key={item.id} className="flex items-center gap-3 py-2">
                      <span className="font-mono text-xs text-ink-500">
                        {item.itemKey}
                      </span>
                      <button
                        type="button"
                        onClick={() => navigate(`/work-items/${item.itemKey}`)}
                        className="min-w-0 flex-1 truncate text-left text-sm text-ink-900"
                      >
                        {item.title}
                      </button>
                      <span className="tnum text-xs text-ink-500">
                        {item.storyPoints != null
                          ? `${item.storyPoints} pts`
                          : "unsized"}
                      </span>
                      <span className="tnum text-xs text-ink-500">
                        {formatHours(item.currentEstimateHours)}
                      </span>
                      {/*
                       * Back to the backlog, not deleted. Nothing about the item changes except
                       * that it is no longer promised for this sprint.
                       */}
                      {can("BACKLOG_RANK") && (
                        <button
                          type="button"
                          title={`Take ${item.itemKey} out of this sprint`}
                          aria-label={`Take ${item.itemKey} out of this sprint`}
                          disabled={removing === item.id}
                          onClick={() => void removeFromSprint(item.id)}
                          className={cn(
                            "shrink-0 rounded p-1 text-ink-400",
                            "hover:bg-red-50 hover:text-red-600",
                            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400",
                            "disabled:cursor-not-allowed disabled:opacity-50",
                          )}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <SprintBoard
                  items={data.items}
                  canEdit={can("WORK_ITEM_EDIT")}
                  onChanged={reload}
                  title={`${data.sprint.name} — board`}
                />
              )}
            </CardBody>
          </Card>

          {finished && data.metrics && (
            <Card>
              <CardHeader
                title="What happened"
                subtitle={`Recorded when the sprint completed, and fixed since. Captured ${new Date(data.metrics.capturedAt).toLocaleDateString()}.`}
              />
              <CardBody>
                <div className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-4">
                  <Figure
                    label="Committed"
                    value={`${data.metrics.committedStoryPoints} pts`}
                  />
                  <Figure
                    label="Delivered"
                    value={`${data.metrics.completedStoryPoints} pts`}
                    tone={
                      data.metrics.commitmentRatio >= 0.9
                        ? "good"
                        : data.metrics.commitmentRatio >= 0.7
                          ? undefined
                          : "bad"
                    }
                  />
                  <Figure
                    label="Of what was promised"
                    value={formatPercent(data.metrics.commitmentRatio)}
                  />
                  <Figure
                    label="Carried over"
                    value={String(data.metrics.carryoverCount)}
                  />

                  <Figure
                    label="Stories"
                    value={`${data.metrics.storiesCompleted} of ${data.metrics.storiesCommitted}`}
                  />
                  <Figure
                    label="Unplanned work"
                    value={`${data.metrics.unplannedStoryPoints} pts`}
                  />
                  <Figure
                    label="Bugs raised in sprint"
                    value={String(data.metrics.bugsAddedDuringSprint)}
                  />
                  <Figure
                    label="Committed against capacity"
                    value={formatPercent(data.metrics.capacityUtilisation)}
                  />
                </div>

                <p className="mt-4 text-xs text-ink-400">
                  These figures do not change. They describe the sprint as it
                  was measured on the day it finished, so re-sizing or
                  reassigning the work afterwards cannot rewrite the record or
                  move the velocity trend.
                </p>
              </CardBody>
            </Card>
          )}

          {/*
           * The names behind the number. "Carried over: 3" is enough to draw a chart and not
           * enough to fix anything; which three, and why each one missed, is what the
           * retrospective actually works from.
           */}
          {finished && <SprintCarryoverCard sprintId={sprintId} />}
        </div>

        <div className="space-y-4">
          {/*
           * Deliberately absent once the sprint is finished.
           *
           * These are live figures, and a completed sprint already has a record of what it did.
           * Showing both meant a finished sprint displaying "completed 21 points" beside a
           * frozen "delivered 5" — both true, and together an invitation to wonder which one
           * counts. Once the sprint is over, the captured record is the answer.
           */}
          {!finished && (
            <Card>
              <CardHeader
                title={planning ? "Does it fit?" : "Progress"}
                subtitle={
                  planning
                    ? "Committed effort against available capacity."
                    : undefined
                }
              />
              <CardBody className="space-y-3">
                {planning ? (
                  <>
                    <Row
                      label="Selected"
                      value={`${data.committedStoryPoints} pts`}
                    />
                    <Row
                      label="Estimated"
                      value={formatHours(data.plannedHours)}
                    />
                    <Row
                      label="Capacity"
                      value={formatHours(data.capacity.availableHours)}
                    />
                    <div className="border-t border-ink-100 pt-3">
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-sm text-ink-500">Fit</span>
                        <StatusBadge tone={fit.tone}>
                          {fit.label}
                          {utilisation > 0 &&
                            ` · ${formatPercent(utilisation)}`}
                        </StatusBadge>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <Row
                      label="Committed"
                      value={`${data.committedStoryPoints} pts`}
                    />
                    <Row
                      label="Completed"
                      value={`${data.completedStoryPoints} pts`}
                    />
                    <Row
                      label="Remaining"
                      value={`${data.remainingStoryPoints} pts`}
                    />
                    <div className="space-y-3 border-t border-ink-100 pt-3">
                      <Row
                        label="Planned hours"
                        value={formatHours(data.plannedHours)}
                      />
                      <Row
                        label="Remaining hours"
                        value={formatHours(data.remainingHours)}
                      />
                      <Row
                        label="Logged hours"
                        value={formatHours(data.loggedHours)}
                        muted
                      />
                    </div>
                    <p className="text-xs text-ink-400">
                      Logged hours are time recorded on timesheets against this
                      sprint's items. Zero means nothing has been logged, not
                      that nothing was spent.
                    </p>
                  </>
                )}
              </CardBody>
            </Card>
          )}

          {running && (
            <Card>
              <CardHeader title="Watch out for" />
              <CardBody className="space-y-3">
                <Row
                  label="Blocked items"
                  value={String(data.blockedItemCount)}
                  tone={data.blockedItemCount > 0 ? "bad" : undefined}
                  icon={
                    data.blockedItemCount > 0 ? (
                      <AlertOctagon className="h-3.5 w-3.5" />
                    ) : undefined
                  }
                />
                <Row
                  label="Unplanned items"
                  value={String(data.unplannedItemCount)}
                  tone={data.unplannedItemCount > 0 ? "bad" : undefined}
                />
                <Row label="Bugs in sprint" value={String(data.bugCount)} />
              </CardBody>
            </Card>
          )}

          <Card>
            <CardHeader
              title="Capacity"
              subtitle={`${data.capacity.headcount} on the project during this sprint`}
              action={<Users className="h-4 w-4 text-ink-400" />}
            />
            <CardBody className="space-y-3">
              <Row
                label="Gross"
                value={formatHours(data.capacity.grossHours)}
              />
              <Row
                label="Less holidays, leave and reserved time"
                value={
                  data.capacity.deductedHours > 0
                    ? `−${formatHours(data.capacity.deductedHours)}`
                    : "nothing"
                }
              />
              <div className="border-t border-ink-100 pt-3">
                <Row
                  label="Available"
                  value={formatHours(data.capacity.availableHours)}
                />
              </div>

              {data.capacity.perPerson.length > 0 && (
                <ul className="space-y-1.5 border-t border-ink-100 pt-3">
                  {data.capacity.perPerson.map((person) => (
                    <li
                      key={person.employeeId}
                      className="flex items-center justify-between gap-2 text-sm"
                    >
                      <span className="truncate text-ink-700">
                        {person.fullName}
                      </span>
                      <span className="tnum shrink-0 text-xs text-ink-500">
                        {person.workingDays}d ·{" "}
                        {formatHours(person.availableHours)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}

              {/*
               * This panel used to list the §40 deductions that could not be made, because
               * leave and reserved time did not exist yet. They do now, so the space goes to
               * the thing §49 actually asks a planning screen to surface: who is committed
               * elsewhere over these same dates.
               */}
              {data.capacity.conflicts.length > 0 && (
                <div className="border-t border-ink-100 pt-3">
                  <p className="text-xs font-medium text-amber-600">
                    Worth resolving before this starts
                  </p>
                  <ul className="mt-1 space-y-0.5">
                    {data.capacity.conflicts.map((note) => (
                      <li key={note} className="text-xs text-ink-500">
                        · {note}
                      </li>
                    ))}
                  </ul>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-1.5 h-6 px-0 text-xs"
                    onClick={() => navigate("/resources")}
                  >
                    Open the resource planner
                  </Button>
                </div>
              )}
            </CardBody>
          </Card>
        </div>
      </div>

      <CancelSprintDrawer
        sprint={cancelling ? data.sprint : null}
        onClose={() => setCancelling(false)}
        onSaved={reload}
      />

      <CompleteSprintDrawer
        sprint={completing ? data.sprint : null}
        onClose={() => setCompleting(false)}
        onSaved={reload}
      />

      <EditSprintDrawer
        sprint={editing ? data.sprint : null}
        onClose={() => setEditing(false)}
        onSaved={reload}
      />

      <AddToSprintDrawer
        sprint={adding ? data.sprint : null}
        projectId={data.sprint.projectId}
        onClose={() => setAdding(false)}
        onSaved={reload}
      />
    </>
  );
}

function Row({
  label,
  value,
  muted,
  tone,
  icon,
}: {
  label: string;
  value: string;
  muted?: boolean;
  tone?: "good" | "bad";
  icon?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="text-ink-500">{label}</span>
      <span
        className={cn(
          "tnum flex items-center gap-1",
          muted
            ? "text-ink-400"
            : tone === "bad"
              ? "font-medium text-red-600"
              : tone === "good"
                ? "text-emerald-600"
                : "text-ink-900",
        )}
      >
        {icon}
        {value}
      </span>
    </div>
  );
}

function Figure({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "good" | "bad";
}) {
  return (
    <div>
      <p className="text-sm text-ink-500">{label}</p>
      <p
        className={cn(
          "tnum font-display mt-0.5 text-xl font-semibold",
          tone === "bad"
            ? "text-red-600"
            : tone === "good"
              ? "text-emerald-600"
              : "text-ink-900",
        )}
      >
        {value}
      </p>
    </div>
  );
}
